import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Trash2, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [manage, setManage] = useState(null);
  const [f, setF] = useState({});
  const [credit, setCredit] = useState("");
  const [balance, setBalance] = useState("");
  const limit = 20;

  const load = () => api.get(`/admin/users?page=${page}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [page]);

  const open = (u) => { setManage(u); setF({ name: u.name || "", email: u.email || "", telegram_id: u.telegram_id ?? "", parent_id: u.parent_id || "" }); setCredit(""); setBalance(""); };
  const patch = async (payload, ok = "Usuário atualizado") => {
    try { const { data: u } = await api.patch(`/admin/users/${manage.id}`, payload); toast.success(ok); setManage(u); load(); return u; }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveProfile = () => patch({
    name: f.name, email: f.email,
    telegram_id: f.telegram_id === "" ? null : Number(f.telegram_id),
    parent_id: f.parent_id === "" ? "none" : f.parent_id,
  });
  const adjustCredit = async () => {
    const amt = Number(credit); if (!amt) return toast.error("Valor inválido");
    try { await api.post("/credits/adjust", { user_id: manage.id, amount: amt, description: "Ajuste manual (admin)" }); toast.success("Créditos ajustados"); setCredit(""); const u = data.items.find(x => x.id === manage.id); load(); setManage((m) => ({ ...m, credits: (Number(m.credits) || 0) + amt })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const adjustBalance = async () => {
    const amt = Number(balance); if (!amt) return toast.error("Valor inválido");
    try { const { data: r } = await api.post(`/admin/users/${manage.id}/balance`, { amount: amt }); toast.success("Saldo ajustado"); setBalance(""); setManage((m) => ({ ...m, balance: r.balance })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Excluir a conta de ${manage.name}? Esta ação é permanente.`)) return;
    try { await api.delete(`/admin/users/${manage.id}`); toast.success("Conta excluída"); setManage(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Usuários</h2>
      <div className="relative w-full sm:w-80">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} placeholder="Buscar por nome ou e-mail..." className="pl-9" data-testid="users-search-input" />
      </div>

      <MobileCards testid="users-cards">
        {data.items.length === 0 && <EmptyCard>Nenhum usuário encontrado.</EmptyCard>}
        {data.items.map((u) => (
          <DataCard
            key={u.id}
            testid={`user-card-${u.id}`}
            title={u.name}
            subtitle={u.email}
            badge={u.blocked ? <Badge variant="destructive">Bloqueado</Badge> : <Badge variant="secondary">Ativo</Badge>}
            rows={[
              { label: "Papel", value: <span className="capitalize">{u.role}</span> },
              { label: "Créditos", value: <span className="font-num">{u.credits}</span> },
              { label: "Saldo", value: <span className="font-num">R$ {Number(u.balance || 0).toFixed(2)}</span> },
              { label: "Vínculos", value: `${u.telegram_id ? "🔗 TG " : ""}${u.store_locked ? "🔒 Loja" : ""}` || "—" },
            ]}
            actions={
              <>
                <Button variant="secondary" size="sm" className="flex-1 gap-1" onClick={() => navigate(`/admin/usuarios/${u.id}`)} data-testid={`user-profile-m-${u.id}`}>
                  <UserCircle className="h-4 w-4" /> Perfil 360
                </Button>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => open(u)} data-testid={`user-manage-m-${u.id}`}>Gerenciar</Button>
              </>
            }
          />
        ))}
      </MobileCards>

      <div className="hidden rounded-lg border border-border bg-card md:block">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nome</TableHead><TableHead>E-mail</TableHead><TableHead>Papel</TableHead><TableHead>Créditos</TableHead><TableHead>Saldo</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium"><div className="flex items-center gap-2">{u.name}
                  {u.telegram_id && <Badge variant="outline" className="gap-1 text-[10px]">🔗 TG</Badge>}
                  {u.store_locked && <Badge variant="outline" className="text-[10px]">🔒 Loja</Badge>}
                </div></TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell className="capitalize">{u.role}</TableCell>
                <TableCell className="font-num">{u.credits}</TableCell>
                <TableCell className="font-num">R$ {Number(u.balance || 0).toFixed(2)}</TableCell>
                <TableCell>{u.blocked ? <Badge variant="destructive">Bloqueado</Badge> : <Badge variant="secondary">Ativo</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" className="gap-1" onClick={() => navigate(`/admin/usuarios/${u.id}`)} data-testid={`user-profile-${u.id}`}><UserCircle className="h-4 w-4" /> Perfil 360</Button>
                    <Button variant="outline" size="sm" onClick={() => open(u)} data-testid={`user-manage-${u.id}`}>Gerenciar</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data.total > limit && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{data.total} usuários</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)} data-testid="users-prev">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page * limit >= data.total} onClick={() => setPage((p) => p + 1)} data-testid="users-next">Próxima</Button>
          </div>
        </div>
      )}

      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="user-manage-dialog">
          <DialogHeader><DialogTitle>Gerenciar {manage?.name}</DialogTitle></DialogHeader>
          {manage && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></div>
                <div className="space-y-1.5"><Label>E-mail</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></div>
                <div className="space-y-1.5"><Label>Telegram ID</Label><Input value={f.telegram_id} onChange={(e) => setF({ ...f, telegram_id: e.target.value })} placeholder="vazio = nenhum" data-testid="user-telegram-input" /></div>
                <div className="space-y-1.5"><Label>Revendedor pai (ID)</Label><Input value={f.parent_id} onChange={(e) => setF({ ...f, parent_id: e.target.value })} placeholder="vazio = sem árvore" data-testid="user-parent-input" /></div>
              </div>
              <Button variant="outline" className="w-full" onClick={saveProfile} data-testid="user-save-profile-btn">Salvar dados</Button>

              <div className="space-y-1.5">
                <Label>Papel</Label>
                <Select value={manage.role} onValueChange={(v) => patch({ role: v }, "Papel atualizado")}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Usuário</SelectItem>
                    <SelectItem value="reseller">Revendedor</SelectItem>
                    <SelectItem value="support">Atendente</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Créditos (atual: {manage.credits})</Label>
                  <div className="flex gap-2"><Input type="number" step="0.1" value={credit} onChange={(e) => setCredit(e.target.value)} placeholder="+/−" data-testid="user-credits-input" /><Button onClick={adjustCredit} data-testid="user-credits-adjust-btn">OK</Button></div>
                </div>
                <div className="space-y-1.5"><Label>Saldo R$ (atual: {Number(manage.balance || 0).toFixed(2)})</Label>
                  <div className="flex gap-2"><Input type="number" step="0.01" value={balance} onChange={(e) => setBalance(e.target.value)} placeholder="+/−" data-testid="user-balance-input" /><Button onClick={adjustBalance} data-testid="user-balance-adjust-btn">OK</Button></div>
                </div>
              </div>

              <label className="flex items-center justify-between rounded-lg border border-border p-3 text-sm">
                <span>Travar na loja do revendedor <span className="text-muted-foreground">(desligue para liberar o Vulpix original)</span></span>
                <Switch checked={!!manage.store_locked} onCheckedChange={(v) => patch({ store_locked: v }, v ? "Travado na loja" : "Liberado")} data-testid="user-store-lock-switch" />
              </label>

              <div className="flex gap-2">
                <Button variant={manage.blocked ? "outline" : "secondary"} className="flex-1" onClick={() => patch({ blocked: !manage.blocked }, manage.blocked ? "Desbloqueado" : "Bloqueado")} data-testid="user-block-btn">
                  {manage.blocked ? "Desbloquear" : "Bloquear"}
                </Button>
                <Button variant="destructive" className="flex-1 gap-2" onClick={del} data-testid="user-delete-btn"><Trash2 className="h-4 w-4" /> Excluir conta</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
